import { useState, useEffect, useCallback, useRef } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Drawer,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import * as api from "../../fileUploaderAdminApi";

const DATA_TYPES = ["string", "integer", "float", "boolean", "datetime", "date"];

const newColumn = (position) => ({
  id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
  name: "",
  position,
  data_type: "string",
  is_required: false,
  allow_missing: true,
  _isNew: true,
});

// ── Upload zone (shown only when creating a new schema) ───────────────────────
function InferFromFileZone({ onInferred }) {
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [inferring, setInferring] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [inferError, setInferError] = useState("");

  const handleFile = async (file) => {
    if (!file) return;
    setInferring(true);
    setInferError("");
    try {
      const result = await api.inferSchemaFromFile(file);
      setUploadedFile(file);
      onInferred(result);
    } catch (err) {
      setInferError(err.message || "Could not parse file. Check the format and try again.");
    } finally {
      setInferring(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  if (uploadedFile && !inferring && !inferError) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          p: 1.5,
          border: 1,
          borderColor: "success.light",
          borderRadius: 1,
          backgroundColor: "success.50",
        }}
      >
        <CheckCircleOutlineIcon color="success" fontSize="small" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" fontWeight={500}>{uploadedFile.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            Columns detected — pre-filled below. You can still edit them.
          </Typography>
        </Box>
        <Button
          size="small"
          onClick={() => {
            setUploadedFile(null);
            setInferError("");
            if (fileInputRef.current) fileInputRef.current.value = "";
            onInferred(null);
          }}
        >
          Remove
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {inferError && <Alert severity="error" sx={{ mb: 1 }}>{inferError}</Alert>}
      <Box
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        sx={{
          border: 2,
          borderStyle: "dashed",
          borderColor: dragOver ? "primary.main" : "divider",
          borderRadius: 1,
          py: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0.5,
          cursor: "pointer",
          backgroundColor: dragOver ? "action.hover" : "transparent",
          transition: "all 0.15s",
        }}
      >
        {inferring ? (
          <>
            <CircularProgress size={24} />
            <Typography variant="caption" color="text.secondary">Detecting columns…</Typography>
          </>
        ) : (
          <>
            <UploadFileOutlinedIcon sx={{ fontSize: 28, color: "text.disabled" }} />
            <Typography variant="body2" color="text.secondary">
              Drop a sample file to detect columns, or{" "}
              <Box component="span" sx={{ color: "primary.main" }}>browse</Box>
            </Typography>
            <Typography variant="caption" color="text.disabled">
              CSV, TSV, TXT, or SAV · file is not stored
            </Typography>
          </>
        )}
      </Box>
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: "none" }}
        onChange={(e) => handleFile(e.target.files[0])}
      />
    </Box>
  );
}

/**
 * Props:
 *   open, onClose, onSave   — standard drawer controls
 *   initialSchema           — schema object to edit (null = create new)
 *   overwriteEnabled        — mirrors template's overwrite_existing flag
 *   hasUploadedFiles        — true if files have already been uploaded to this template
 *
 * When overwriteEnabled=false AND hasUploadedFiles=true, existing columns are
 * locked: name, data type, and delete are disabled. New columns can still be added.
 */
export default function FileSchemaEditor({
  open,
  onClose,
  onSave,
  initialSchema,
  overwriteEnabled = true,
  hasUploadedFiles = false,
  saving = false,
  saveError: externalSaveError = "",
}) {
  const isNew = !initialSchema?.name;
  // Restrict edits on existing columns when files have been uploaded with overwrite off
  const editingRestricted = !isNew && !overwriteEnabled && hasUploadedFiles;

  const [schemaName, setSchemaName] = useState("");
  const [strict, setStrict] = useState(false);
  const [columns, setColumns] = useState([]);
  const [sampleValues, setSampleValues] = useState(null);
  const [errors, setErrors] = useState({});
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    if (open) {
      setSchemaName(initialSchema?.name ?? "");
      setStrict(initialSchema?.strict ?? false);
      setColumns(
        initialSchema?.columns
          ? initialSchema.columns.map((c) => ({ ...c }))
          : [newColumn(0)]
      );
      setSampleValues(null);
      setErrors({});
      setSaveError("");
    }
  }, [open, initialSchema]);

  const handleInferred = useCallback((result) => {
    if (!result) {
      // No file selected — reset to a single blank row
      setColumns([newColumn(0)]);
      setSampleValues(null);
      return;
    }
    setSampleValues(result.sample_values ?? null);
    setColumns(
      result.columns.map((col, idx) => ({
        id: `inferred-${idx}`,
        name: col.name,
        position: col.position ?? idx,
        data_type: col.inferred_type || "string",
        is_required: col.is_required ?? false,
        allow_missing: !col.is_required,
      }))
    );
  }, []);

  const updateColumn = useCallback((id, field, value) => {
    setColumns((cols) =>
      cols.map((c) => {
        if (c.id !== id) return c;
        const updates = { [field]: value };
        if (field === "is_required" && value === true) updates.allow_missing = false;
        if (field === "allow_missing" && value === true) updates.is_required = false;
        return { ...c, ...updates };
      })
    );
  }, []);

  const addColumn = () => {
    setColumns((cols) => [...cols, newColumn(cols.length)]);
  };

  const removeColumn = (id) => {
    setColumns((cols) => {
      const filtered = cols.filter((c) => c.id !== id);
      return filtered.map((c, idx) => ({ ...c, position: idx }));
    });
  };

  const moveColumn = (id, direction) => {
    setColumns((cols) => {
      const idx = cols.findIndex((c) => c.id === id);
      const swapIdx = idx + direction;
      if (swapIdx < 0 || swapIdx >= cols.length) return cols;
      const next = [...cols];
      [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
      return next.map((c, i) => ({ ...c, position: i }));
    });
  };

  const validate = () => {
    const errs = {};
    if (!schemaName.trim()) errs.schemaName = "Schema name is required.";
    const names = columns.map((c) => c.name.trim());
    if (names.some((n) => !n)) errs.columns = "All columns must have a name.";
    const dupes = names.filter((n, i) => n && names.indexOf(n) !== i);
    if (dupes.length) errs.columns = `Duplicate column name(s): ${[...new Set(dupes)].join(", ")}`;
    return errs;
  };

  // The parent controls when the drawer closes, to support async save flows.
  const handleSave = () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    onSave({
      name: schemaName.trim(),
      strict,
      // Strip internal tracking flag before passing columns to the parent.
      columns: columns.map(({ _isNew, ...col }) => col),
    });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100vw", md: 720 }, p: 0 } }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2,
          borderBottom: 1,
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Typography variant="h6">
          {isNew ? "New Schema" : "Edit Schema"}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 3 }}>
        <Stack spacing={3}>
          {(externalSaveError || saveError) && (
            <Alert severity="error">{externalSaveError || saveError}</Alert>
          )}

          {/* Contextual schema safety alert — only shown when editing an existing schema */}
          {!isNew && (
            editingRestricted ? (
              <Alert severity="info" icon={false}>
                <Typography variant="body2" fontWeight={500} gutterBottom>
                  Some edits are restricted
                </Typography>
                Files have been uploaded to this template and overwrite is disabled. You can
                add new columns freely, but <strong>existing columns cannot be renamed, have
                their type changed, or be deleted</strong> — doing so would break files already
                in the pipeline. To unlock full editing, enable "Overwrite existing files" on
                the template.
              </Alert>
            ) : overwriteEnabled ? (
              <Alert severity="info" icon={false}>
                Overwrite is enabled. All column edits are permitted, including renaming,
                type changes, and deletion.
              </Alert>
            ) : (
              <Alert severity="info" icon={false}>
                No files have been uploaded yet. All column edits are permitted. Once files
                are uploaded, edits will be restricted unless overwrite is enabled.
              </Alert>
            )
          )}

          {/* Upload zone — new schemas only */}
          {isNew && <InferFromFileZone onInferred={handleInferred} />}

          <TextField
            label="Schema Name"
            value={schemaName}
            onChange={(e) => setSchemaName(e.target.value)}
            required
            fullWidth
            error={!!errors.schemaName}
            helperText={errors.schemaName}
          />

          <FormControlLabel
            control={
              <Switch
                checked={strict}
                onChange={(e) => setStrict(e.target.checked)}
                size="small"
              />
            }
            label={
              <Box>
                <Typography variant="body2">Strict mode</Typography>
                <Typography variant="caption" color="text.secondary">
                  When enabled, files with columns not defined in the schema are rejected. When off, extra columns are ignored.
                </Typography>
              </Box>
            }
          />

          <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography variant="subtitle2">Columns</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={addColumn}>
                Add Column
              </Button>
            </Box>

            {errors.columns && (
              <Alert severity="error" sx={{ mb: 1 }}>{errors.columns}</Alert>
            )}

            <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
              <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "grey.50" }}>
                    {/* Order: up/down arrows + row number */}
                    <TableCell sx={{ width: 52, p: 0.5, textAlign: "center" }}>#</TableCell>
                    {/* Column name — takes remaining space */}
                    <TableCell>Column Name</TableCell>
                    <TableCell sx={{ width: 120 }}>Type</TableCell>
                    <TableCell sx={{ width: 58, textAlign: "center", px: 0.5 }}>Req</TableCell>
                    <TableCell sx={{ width: 68, textAlign: "center", px: 0.5 }}>Missing</TableCell>
                    <TableCell sx={{ width: 40, p: 0 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {columns.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ color: "text.secondary", py: 3 }}>
                        No columns yet. Click "Add Column" to start.
                      </TableCell>
                    </TableRow>
                  )}
                  {columns.map((col, idx) => (
                    <TableRow key={col.id} sx={{ "&:last-child td": { border: 0 } }}>
                      {/* Up/down + row number */}
                      <TableCell sx={{ textAlign: "center", p: 0, width: 52 }}>
                        <Stack direction="column" alignItems="center" spacing={0}>
                          <IconButton size="small" onClick={() => moveColumn(col.id, -1)} disabled={idx === 0} sx={{ p: 0.25 }}>
                            <KeyboardArrowUpIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                          <Typography variant="caption" color="text.secondary" lineHeight={1}>
                            {idx + 1}
                          </Typography>
                          <IconButton size="small" onClick={() => moveColumn(col.id, 1)} disabled={idx === columns.length - 1} sx={{ p: 0.25 }}>
                            <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Stack>
                      </TableCell>

                      {/* Column name */}
                      <TableCell sx={{ py: 0.75 }}>
                        <Tooltip
                          title={editingRestricted && !col._isNew ? "Column names cannot be changed after files have been uploaded with overwrite disabled." : ""}
                          disableHoverListener={!editingRestricted || !!col._isNew}
                        >
                          <span>
                            <TextField
                              size="small"
                              value={col.name}
                              onChange={(e) => updateColumn(col.id, "name", e.target.value)}
                              placeholder="column_name"
                              fullWidth
                              disabled={editingRestricted && !col._isNew}
                              inputProps={{ style: { fontFamily: "monospace", fontSize: 13 } }}
                            />
                          </span>
                        </Tooltip>
                      </TableCell>

                      {/* Type */}
                      <TableCell sx={{ width: 120, py: 0.75 }}>
                        <Tooltip
                          title={editingRestricted && !col._isNew ? "Data types cannot be changed after files have been uploaded with overwrite disabled." : ""}
                          disableHoverListener={!editingRestricted || !!col._isNew}
                        >
                          <span>
                            <FormControl size="small" fullWidth disabled={editingRestricted && !col._isNew}>
                              <Select
                                value={col.data_type}
                                onChange={(e) => updateColumn(col.id, "data_type", e.target.value)}
                              >
                                {DATA_TYPES.map((t) => (
                                  <MenuItem key={t} value={t}>{t}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </span>
                        </Tooltip>
                      </TableCell>

                      {/* Required */}
                      <TableCell align="center" sx={{ width: 58, px: 0.5 }}>
                        <Checkbox
                          size="small"
                          checked={col.is_required}
                          onChange={(e) => updateColumn(col.id, "is_required", e.target.checked)}
                        />
                      </TableCell>

                      {/* Allow missing */}
                      <TableCell align="center" sx={{ width: 68, px: 0.5 }}>
                        <Checkbox
                          size="small"
                          checked={col.allow_missing}
                          onChange={(e) => updateColumn(col.id, "allow_missing", e.target.checked)}
                        />
                      </TableCell>

                      {/* Delete */}
                      <TableCell align="center" sx={{ width: 40, p: 0 }}>
                        <Tooltip title={
                          editingRestricted && !col._isNew
                            ? "Existing columns cannot be deleted after files have been uploaded with overwrite disabled."
                            : "Remove column"
                        }>
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => removeColumn(col.id)}
                              disabled={editingRestricted && !col._isNew}
                              sx={{ p: 0.5 }}
                            >
                              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
              Tip: "Required" means the column must exist in the file. "Allow Missing" controls whether empty cells are valid.
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderTop: 1,
          borderColor: "divider",
          display: "flex",
          gap: 2,
          justifyContent: "flex-end",
          flexShrink: 0,
        }}
      >
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={20} sx={{ color: "inherit" }} /> : "Apply Schema"}
        </Button>
      </Box>
    </Drawer>
  );
}
